import {
    Component,
    Input,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy,
} from '@angular/core';
import { AttachmentEventService } from '../../../services/attachment-event/attachment-event.service';
import {
    ColDef,
    GridApi,
    GridOptions,
    GridReadyEvent,
} from 'ag-grid-community';
import { nameOf } from '../../../../../../../core/functions/string-helpers';
import { GridHelper } from '../../../../../../../core/models/grid/grid-helper';
import { GridToolbarOptions } from '../../../../../../../shared/components/grid/grid-toolbar/models/grid-toolbar-options';
import { AttachmentService } from '../../../services/attachment/attachment.service';
import { filter, Subject, takeUntil, tap } from 'rxjs';
import { AttachmentResponse } from '../../../models/attachment/attachment-response';
import { AgGridAngular } from 'ag-grid-angular';
import { GridToolbarComponent } from '../../../../../../../shared/components/grid/grid-toolbar/grid-toolbar.component';
import { MatButtonModule } from '@angular/material/button';
import { AttachmentManagementService } from '../../../services/attachment-management/attachment-management.service';
import { AttachmentFilenameCellRendererComponent } from '../../../cell-renderers/attachment-management/attachment-filename-cell-renderer/attachment-filename-cell-renderer.component';
import { DropdownService } from '../../../../../../../core/services/resources/dropdown.service';
import { DropdownOption } from '../../../../../../../core/models/dropdown-option';
import { ApplicationFormControlManagerService } from '../../../../../shared/services/application-form-control-manager/application-form-control-manager.service';
import { MatIconModule } from '@angular/material/icon';

@Component({
    selector: 'app-attachment-grid',
    templateUrl: './attachment-grid.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        AgGridAngular,
        GridToolbarComponent,
        MatButtonModule,
        MatIconModule,
    ],
})
export class AttachmentGridComponent implements OnInit, OnDestroy {
    private destroy$: Subject<void> = new Subject<void>();

    @Input() applicationId!: string;

    constructor(
        private readonly dropdownService: DropdownService,
        private readonly attachmentService: AttachmentService,
        private readonly attachmentEventService: AttachmentEventService,
        private readonly attachmentManagementService: AttachmentManagementService,
        private readonly applicationFormControlManagerService: ApplicationFormControlManagerService,
    ) {}

    attachmentTypeCodeOptions: DropdownOption[] = [];

    context: unknown;
    rowData: AttachmentResponse[] = [];
    gridApi: GridApi | undefined;

    columnDefs: ColDef[] = [
        {
            headerName: 'File Name',
            field: nameOf<AttachmentResponse>('filename'),
            cellRenderer: AttachmentFilenameCellRendererComponent,
            flex: 2,
        },
        {
            headerName: 'Attachment Type',
            field: nameOf<AttachmentResponse>('attachmentTypeCode'),
            valueFormatter: (params) =>
                this.formatAttachmentType(params.data.attachmentTypeCode),
            valueGetter: (params) =>
                this.formatAttachmentType(params.data.attachmentTypeCode),
            flex: 1,
        },
        {
            headerName: 'File Description',
            field: nameOf<AttachmentResponse>('description'),
            flex: 2,
        },
    ];

    gridToolbarOptions: GridToolbarOptions = {
        ...GridHelper.defaultToolbarOptions,
        quickFilter: false,
        clearFilters: false,
        exportCsv: false,
        primaryGridAction: {
            title: 'Add or Edit Attachments',
            disabled: this.applicationFormControlManagerService.isReadOnly(),
            action: () =>
                this.attachmentManagementService.displayAttachmentManagementDialog(
                    this.applicationId,
                ),
        },
    };

    gridOptions: GridOptions = {
        ...GridHelper.defaultGridOptions,
    };

    ngOnInit(): void {
        this.setupListeners();
        this.loadAttachmentCodeOptions();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onGridReady(params: GridReadyEvent<unknown>): void {
        this.gridApi = params.api;

        this.loadGridData();
    }

    /**
     * Sets up listeners for attachment-related events to refresh the grid data when changes occur.
     */
    private setupListeners(): void {
        this.attachmentEventService.applicationAttachmentsChanged$
            .pipe(
                takeUntil(this.destroy$),
                tap(() => this.loadGridData()),
            )
            .subscribe();
    }

    /**
     * Loads the grid data by fetching attachments for the specified application ID and transforming them for display.
     */
    private loadGridData(): void {
        this.attachmentService
            .getAll(this.applicationId)
            .pipe(
                takeUntil(this.destroy$),
                tap((rowData: AttachmentResponse[]) => {
                    this.rowData = rowData;
                }),
                filter(() => {
                    return !!this.gridApi;
                }),
            )
            .subscribe(() => {
                this.gridApi!.setGridOption('rowData', this.rowData);
                this.gridApi!.setGridOption('columnDefs', this.columnDefs);
            });
    }

    /**
     * Formats the attachment type code into a human-readable label using the loaded dropdown options.
     *
     * @param code the attachment type code to format.
     * @returns the formatted attachment type label, or the original code if no match is found.
     */
    private formatAttachmentType(code: string): string {
        const dropdownOption = this.attachmentTypeCodeOptions.find(
            (option) => option.value === code,
        );

        return dropdownOption ? dropdownOption.label : code;
    }

    /**
     * Loads the attachment type code options from the dropdown service to be used for formatting in the grid.
     */
    private loadAttachmentCodeOptions(): void {
        this.dropdownService
            .getAttachmentCodeOptions()
            .pipe(
                tap((attachmentCodeOptions: DropdownOption[]) => {
                    this.attachmentTypeCodeOptions = attachmentCodeOptions;
                }),
            )
            .subscribe();
    }
}
